import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./ServiceDetail.css"; // Reusing the same CSS

const servicesData = [
  {
    id: 1,
    title: "Workday Advisory",
    icon: "🎯",
    description: "Strategic guidance and roadmap planning for successful Workday implementation.",
    color: "linear-gradient(135deg, #0073ff 0%, #2980b9 100%)",
    detailedDescription: "Our Workday Advisory services provide expert strategic guidance throughout your Workday journey. We help you develop a comprehensive roadmap aligned with your business objectives, ensuring maximum ROI from your Workday investment. Our certified advisors bring deep industry expertise to optimize your HCM, Financials, and Planning processes.",
    features: [
      "Strategic Implementation Planning",
      "Business Process Optimization",
      "Roadmap & Future State Planning",
      "ROI Analysis & Business Case",
      "Change Management Strategy",
      "System Architecture Design",
      "Vendor Selection Assistance",
      "Governance Framework Design"
    ],
    benefits: [
      "40% faster implementation timelines",
      "60% reduction in implementation risk",
      "Optimized business processes",
      "Increased stakeholder alignment",
      "Clear ROI measurement framework",
      "Future-proof architecture"
    ],
    caseStudy: "Guided Fortune 500 retail company through global Workday implementation, achieving 45% faster deployment and 99% user satisfaction.",
    methodologies: [
      "Discovery & Assessment Workshop",
      "Business Process Review",
      "Gap Analysis",
      "Solution Design Sessions",
      "Implementation Roadmap",
      "Change Management Plan"
    ],
    deliverables: [
      "Strategic Implementation Plan",
      "Business Requirements Document",
      "ROI Analysis Report",
      "Governance Framework",
      "Change Management Strategy",
      "Risk Assessment Matrix"
    ],
    technologies: ["Workday HCM", "Workday Financials", "Workday Planning", "Workday Prism Analytics"],
    duration: "4-8 weeks",
    engagementModel: "Fixed Price or Time & Materials"
  },
  {
    id: 2,
    title: "Workday AMS",
    icon: "🛡️",
    description: "24/7 Application Management Services for continuous optimization.",
    color: "linear-gradient(135deg, #3498db 0%, #2c3e50 100%)",
    detailedDescription: "Our Workday AMS provides comprehensive support and maintenance services to ensure your Workday system runs smoothly 24/7. We offer proactive monitoring, incident management, and continuous optimization to keep your operations running at peak performance.",
    features: [
      "24/7 Production Support",
      "Incident & Problem Management",
      "Enhancement & Updates",
      "Security & Compliance",
      "Performance Monitoring",
      "Backup & Disaster Recovery",
      "Release Management",
      "User Access Management"
    ],
    benefits: [
      "99.9% system uptime guarantee",
      "50% faster incident resolution",
      "Proactive issue prevention",
      "Reduced total cost of ownership",
      "Continuous system optimization",
      "Enhanced security posture"
    ],
    caseStudy: "Managed AMS for financial services firm with 30,000+ users, achieving 99.95% uptime and 40% cost reduction.",
    methodologies: [
      "ITIL-based Service Management",
      "Proactive Monitoring",
      "Root Cause Analysis",
      "Continuous Improvement",
      "Quarterly Business Reviews",
      "Performance Benchmarking"
    ],
    deliverables: [
      "Service Level Agreement",
      "Monthly Service Reports",
      "Performance Dashboards",
      "Security Compliance Reports",
      "Backup & Recovery Plans",
      "System Health Check Reports"
    ],
    technologies: ["Workday Studio", "Workday Extend", "Workday Integration Cloud", "Workday Report Writer"],
    duration: "Ongoing",
    engagementModel: "Monthly Subscription"
  },
  {
    id: 3,
    title: "Workday Experts on Demand",
    icon: "👥",
    description: "Flexible access to certified Workday experts for your specific needs.",
    color: "linear-gradient(135deg, #1abc9c 0%, #16a085 100%)",
    detailedDescription: "Get immediate access to certified Workday experts for your specific project needs. Our flexible engagement model allows you to scale resources up or down based on your requirements, ensuring you have the right expertise at the right time.",
    features: [
      "Certified Workday Consultants",
      "Flexible Engagement Models",
      "Specialized Expertise Access",
      "Project-Based Staffing",
      "Knowledge Transfer",
      "Mentoring & Coaching",
      "Ad-hoc Consultation",
      "Emergency Support"
    ],
    benefits: [
      "On-demand expertise",
      "Reduced hiring costs",
      "Faster project completion",
      "Access to niche skills",
      "Flexible scaling",
      "Knowledge retention"
    ],
    caseStudy: "Provided expert resources for healthcare client's Workday upgrade, completing project 30% under budget.",
    methodologies: [
      "Skill Assessment & Matching",
      "Onboarding & Integration",
      "Weekly Progress Reviews",
      "Knowledge Transfer Sessions",
      "Quality Assurance Checks",
      "Exit Handover Process"
    ],
    deliverables: [
      "Resource Allocation Plan",
      "Weekly Status Reports",
      "Knowledge Transfer Documents",
      "Project Completion Reports",
      "Skill Gap Analysis",
      "Recommendation Reports"
    ],
    technologies: ["Workday Core HCM", "Workday Advanced Compensation", "Workday Talent & Performance", "Workday Learning"],
    duration: "Flexible",
    engagementModel: "Time & Materials"
  },
  {
    id: 4,
    title: "Workday Test Automation",
    icon: "🤖",
    description: "Automated testing solutions ensuring quality and speed.",
    color: "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
    detailedDescription: "Accelerate your Workday deployments with our comprehensive test automation solutions. We ensure quality, reduce risks, and enable continuous delivery through automated testing frameworks specifically designed for Workday environments.",
    features: [
      "End-to-End Regression Testing",
      "Performance & Load Testing",
      "Integration Testing",
      "Continuous Testing Pipeline",
      "Test Data Management",
      "Defect Tracking & Reporting",
      "Test Scenario Library",
      "Automated Reporting"
    ],
    benefits: [
      "80% faster testing cycles",
      "95% test automation coverage",
      "Early defect detection",
      "Reusable test assets",
      "Reduced manual effort",
      "Improved deployment confidence"
    ],
    caseStudy: "Implemented test automation for manufacturing client, reducing deployment cycles from 3 months to 3 weeks.",
    methodologies: [
      "Test Strategy Development",
      "Test Case Design",
      "Automation Framework Setup",
      "Continuous Integration",
      "Defect Management",
      "Test Environment Management"
    ],
    deliverables: [
      "Test Automation Strategy",
      "Automated Test Scripts",
      "Test Data Sets",
      "Test Execution Reports",
      "Defect Reports",
      "Performance Test Results"
    ],
    technologies: ["Selenium", "Postman", "JMeter", "Workday API", "Python", "Java"],
    duration: "4-12 weeks",
    engagementModel: "Fixed Price or Time & Materials"
  },
  {
    id: 5,
    title: "Workday Certifications",
    icon: "🏆",
    description: "Official Workday certification programs for your team.",
    color: "linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)",
    detailedDescription: "Get your team certified with official Workday certification programs. We provide comprehensive training and certification support for various Workday modules, ensuring your organization has the certified expertise needed for success.",
    features: [
      "Workday Pro Certification",
      "HCM Core Certification",
      "Financials Certification",
      "Reporting Certification",
      "Integration Certification",
      "Security Certification",
      "Recruiting Certification",
      "Advanced Compensation Certification"
    ],
    benefits: [
      "Official Workday certifications",
      "Increased system proficiency",
      "Better support quality",
      "Enhanced career development",
      "Industry recognition",
      "Improved system adoption"
    ],
    caseStudy: "Certified 100+ consultants for global consulting firm, achieving 95% certification success rate.",
    methodologies: [
      "Pre-assessment Tests",
      "Structured Learning Paths",
      "Hands-on Labs",
      "Mock Exams",
      "One-on-One Coaching",
      "Post-training Support"
    ],
    deliverables: [
      "Customized Learning Plan",
      "Training Materials",
      "Practice Exercises",
      "Mock Exam Results",
      "Certification Exam Vouchers",
      "Training Completion Certificates"
    ],
    technologies: ["Workday Learning", "Workday Community", "Workday Sandbox", "Online Proctoring Tools"],
    duration: "2-8 weeks per certification",
    engagementModel: "Fixed Price"
  },
  {
    id: 6,
    title: "Workday Training",
    icon: "📚",
    description: "Comprehensive training programs for all user levels.",
    color: "linear-gradient(135deg, #f39c12 0%, #d35400 100%)",
    detailedDescription: "Empower your team with comprehensive Workday training programs tailored for all user levels. From administrators to end-users, we offer customized training solutions that ensure maximum adoption and ROI from your Workday investment.",
    features: [
      "Administrator Training",
      "End-User Workshops",
      "Train-the-Trainer Programs",
      "Customized Curriculum",
      "eLearning Modules",
      "Ongoing Support",
      "Role-Based Training",
      "Process-Specific Training"
    ],
    benefits: [
      "95% user adoption rates",
      "70% fewer support tickets",
      "Internal expertise building",
      "Continuous learning culture",
      "Improved productivity",
      "Reduced dependency on consultants"
    ],
    caseStudy: "Trained 10,000+ users across global organization, achieving 98% adoption within first quarter.",
    methodologies: [
      "Training Needs Analysis",
      "Curriculum Development",
      "Interactive Workshops",
      "Hands-on Exercises",
      "Assessment & Feedback",
      "Post-training Evaluation"
    ],
    deliverables: [
      "Training Needs Assessment",
      "Customized Training Materials",
      "eLearning Modules",
      "Training Videos",
      "User Guides & Manuals",
      "Training Effectiveness Report"
    ],
    technologies: ["Workday Learning", "Zoom/Teams", "LMS Integration", "Screen Recording Tools", "Interactive PDFs"],
    duration: "2-6 weeks",
    engagementModel: "Fixed Price"
  }
];

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Find the service by id
    const foundService = servicesData.find(s => s.id === parseInt(id));
    setService(foundService);
    setLoading(false);
    
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="service-detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading service details...</p>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="service-detail-not-found">
        <h2>Service Not Found</h2>
        <p>The service you're looking for doesn't exist.</p>
        <button onClick={() => navigate("/services")} className="btn-back-to-services">
          Back to Services
        </button>
      </div>
    );
  }

  return (
    <div className="service-detail-page">
      {/* Header Section */}
      <section className="service-detail-header" style={{ background: service.color + '10' }}>
        <div className="service-detail-header-container">
          <button onClick={() => navigate("/services")} className="back-button">
            ← Back to Services
          </button>
          
          <div className="service-header-content">
            <div className="service-header-icon" style={{ background: service.color }}>
              {service.icon}
            </div>
            <div className="service-header-text">
              <h1>{service.title}</h1>
              <p className="service-header-description">{service.description}</p>
              <div className="service-header-meta">
                <span className="meta-item">
                  <span className="meta-label">Duration:</span>
                  <span className="meta-value">{service.duration}</span>
                </span>
                <span className="meta-item">
                  <span className="meta-label">Engagement:</span>
                  <span className="meta-value">{service.engagementModel}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="service-overview-section">
        <div className="service-overview-container">
          <div className="overview-content">
            <h2>Service Overview</h2>
            <p>{service.detailedDescription}</p>
          </div>
          
          <div className="overview-highlights">
            <div className="highlight-card">
              <div className="highlight-icon">⚡</div>
              <h3>Key Benefits</h3>
              <ul>
                {service.benefits.slice(0, 3).map((benefit, index) => (
                  <li key={index}>{benefit}</li>
                ))}
              </ul>
            </div>
            
            <div className="highlight-card">
              <div className="highlight-icon">🎯</div>
              <h3>Methodologies</h3>
              <ul>
                {service.methodologies.slice(0, 3).map((method, index) => (
                  <li key={index}>{method}</li>
                ))}
              </ul>
            </div>
            
            <div className="highlight-card">
              <div className="highlight-icon">📋</div>
              <h3>Technologies</h3>
              <div className="tech-tags">
                {service.technologies.map((tech, index) => (
                  <span key={index} className="tech-tag">{tech}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Features Section */}
      <section className="service-features-detail-section">
        <div className="features-detail-container">
          <h2>Comprehensive Features</h2>
          <div className="features-grid">
            {service.features.map((feature, index) => (
              <div key={index} className="feature-item">
                <div className="feature-icon">✓</div>
                <div className="feature-content">
                  <h4>{feature}</h4>
                  <p>Detailed description of how this feature benefits your organization.</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="service-case-study-section">
        <div className="case-study-container">
          <h2>Success Story</h2>
          <div className="case-study-card">
            <div className="case-study-header">
              <div className="case-study-icon">📈</div>
              <div className="case-study-title">
                <h3>Client Success: {service.title}</h3>
                <p className="case-study-industry">Industry: Retail/Manufacturing/Healthcare</p>
              </div>
            </div>
            <div className="case-study-content">
              <p>{service.caseStudy}</p>
              
              <div className="case-study-metrics">
                <div className="metric-card">
                  <div className="metric-value">95%</div>
                  <div className="metric-label">Success Rate</div>
                </div>
                <div className="metric-card">
                  <div className="metric-value">40%</div>
                  <div className="metric-label">Faster Deployment</div>
                </div>
                <div className="metric-card">
                  <div className="metric-value">99%</div>
                  <div className="metric-label">User Satisfaction</div>
                </div>
                <div className="metric-card">
                  <div className="metric-value">30%</div>
                  <div className="metric-label">Cost Reduction</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Deliverables Section */}
      <section className="service-deliverables-section">
        <div className="deliverables-container">
          <h2>Key Deliverables</h2>
          <div className="deliverables-list">
            {service.deliverables.map((deliverable, index) => (
              <div key={index} className="deliverable-item">
                <div className="deliverable-number">{index + 1}</div>
                <div className="deliverable-content">
                  <h4>{deliverable}</h4>
                  <p>Comprehensive document detailing requirements and specifications.</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-detail-cta">
        <div className="service-cta-container">
          <h2>Ready to Get Started?</h2>
          <p>Contact us to discuss how our {service.title} service can help your organization achieve its goals.</p>
          
          <div className="service-cta-buttons">
            <button 
              className="btn-service-contact" 
              onClick={() => navigate("/contact", { state: { service: service.title } })}
            >
              Request Consultation
            </button>
           
          </div>
          
          <div className="service-cta-info">
            <div className="cta-info-item">
              <div className="info-icon">📞</div>
              <div className="info-content">
                <h4>Get in Touch</h4>
                <p>Schedule a 30-minute discovery call</p>
              </div>
            </div>
            <div className="cta-info-item">
              <div className="info-icon">📅</div>
              <div className="info-content">
                <h4>Quick Start</h4>
                <p>Begin engagement within 2 weeks</p>
              </div>
            </div>
            <div className="cta-info-item">
              <div className="info-icon">💼</div>
              <div className="info-content">
                <h4>Flexible Engagement</h4>
                <p>{service.engagementModel} models available</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="related-services-section">
        <div className="related-services-container">
          <h2>Related Services</h2>
          <div className="related-services-grid">
            {servicesData
              .filter(s => s.id !== service.id)
              .slice(0, 3)
              .map(relatedService => (
                <div 
                  key={relatedService.id} 
                  className="related-service-card"
                  onClick={() => navigate(`/services/${relatedService.id}`)}
                >
                  <div className="related-service-icon" style={{ background: relatedService.color }}>
                    {relatedService.icon}
                  </div>
                  <h4>{relatedService.title}</h4>
                  <p>{relatedService.description}</p>
                  <button className="btn-related-service">
                    Learn More →
                  </button>
                </div>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;